import React, {useState, useEffect, useRef} from 'react';
import classnames from 'classnames';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import {Field} from 'formik';
import {useDrop} from 'react-dnd';
import {NativeTypes} from 'react-dnd-html5-backend';
import {DndProvider} from 'react-dnd'
import {HTML5Backend} from 'react-dnd-html5-backend';
import ButtonCommon from '../ButtonCommon';
import EmptyView from '../Empty';
import {getFileTypeIcon} from '../../utils/files/getFileTypeIcon';
import fileIcon from '../../assets/file_present-24px.svg';
import ok from '../../assets/check-black.svg';
import remove from '../../assets/delete-24px.svg';
import styles from './styles.module.scss';

const FieldPasteFile = ({
                          triggerType,
                          fileUploaded,
                          customIcon = fileIcon,
                          customLabel = 'צרף',
                          ...rest
                        }) => {
  const [showModal, setShowModal] = useState(false);
  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  const getTrigger = () => {
    switch (triggerType) {
      case 'option':
        return (
          <div
            className={styles.optionOpenModal}
            onClick={handleShowModal}
          >
            <img src={customIcon} alt="file"/>
            <span className={styles.optionLabel}>{customLabel}</span>
          </div>
        )
      case 'button':
      default:
        const buttonLabel = fileUploaded ? 'הוסף' : customLabel;
        const iconSrc = fileUploaded ? ok : customIcon;
        return (
          <ButtonCommon
            label={buttonLabel}
            icon={iconSrc}
            onClick={handleShowModal}
            customContainerStyles={styles.buttonOpenModal}
            customIconStyles={styles.iconUpload}
            customLabelStyles={styles.buttonOpenModalLabel}
          />
        )
    }
  };

  return (
    <>
      {getTrigger()}
      <Modal
        isOpen={showModal}
        onRequestClose={handleCloseModal}
        className={styles.modal}
        overlayClassName={styles.overlay}
        shouldCloseOnOverlayClick={false}
      >
        <DndProvider backend={HTML5Backend}>
          <UploadFile
            handleCloseModal={handleCloseModal}
            {...rest}
          />
        </DndProvider>
      </Modal>
    </>
  )
};


const UploadFile = ({
                      fieldId,
                      multiple = false,
                      fileUploaded,
                      onFileSelected,
                      setFieldValue,
                      handleCloseModal,
                      ...rest
                    }) => {
  const [files, setFiles] = useState([]);
  // drag-drop
  const [{canDrop, isOver}, drop] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      drop(item) {
        if (item) {
          if (multiple) {
            setFiles(item.files);
          } else {
            setFiles([item.files[0]])
          }
        }
      },
      canDrop(item) {
        return true
      },
      collect: (monitor) => {
        return {
          isOver: monitor.isOver(),
          canDrop: monitor.canDrop(),
        }
      },
    }),
    [setFiles],
  );
  // copy-paste
  useEffect(() => {
    const paste = (evt) => {
      const clipboardItems = evt.clipboardData.items;
      const items = [].slice.call(clipboardItems)
      if (items.length === 0) {
        return;
      }
      if (multiple) {
        const array = items.map(item => {
          return item.getAsFile();
        })
        setFiles(array)
      } else {
        const blob = items[0].getAsFile();
        setFiles([blob])
      }

    };
    document.addEventListener('paste', paste);
    return () => {
      document.removeEventListener('paste', paste);
    };
  }, []);

  const selectedFile = useRef(null);
  const handleChange = (fieldId) => {
    const selected = multiple ? files : files[0];

    setFieldValue(fieldId, selected);
    if (onFileSelected) onFileSelected(selected);
  };

  const onRemoveFile = (fileName) => {
    const newFilesArray = files.filter(item => item.name !== fileName);
    setFiles(newFilesArray);
  };

  const EditableFileName = ({file, index}) => {
    const cleanName = Boolean(file.name)
      ? file.name.split('.')[0]
      : '';
    const extension = Boolean(file.name)
      ? file.name.split('.')[1]
      : '';

    return (
      <input
        type="text"
        defaultValue={cleanName}
        className={styles.fileNameInput}
        onBlur={(e) => {
          const editedFiles = files.map((item, i) => {
            if (index === i) {
              const newName = Boolean(e.target.value)
                ? [e.target.value, extension].join('.')
                : item.name;
              return new File([item], newName, {type: item.type});
            } else return item
          })
          setFiles(editedFiles)
        }}
      />
    )
  };

  return (
    <div className={styles.modalContainer}>
      <Field id={fieldId} name={fieldId}>
        {({}) => (
          <label>
            <input
              id={fieldId}
              type="file"
              ref={selectedFile}
              multiple={multiple}
              hidden
              {...rest}
            />
          </label>
        )}
      </Field>
      <h2 className={styles.modalHeader}>{'העלאת קבצים'}</h2>
      <div
        ref={drop}
        className={classnames(styles.dragDropContainer, {[styles.dragDropActive]: canDrop && isOver})}
      >
        <div className={styles.dragDropHeader}>
          <img src={fileIcon} alt="file" className={styles.dragDropIcon}/>
          <div>{'ניתן לגרור קבצים ישירות לכאן או להדביק אותם'}</div>
        </div>
      </div>
      <div className={styles.fileListContainer}>
        {files.filter(Boolean).length > 0 ? (
          <>
            {files.map((file, index) => (
              <div className={styles.fileItem} key={index}>
                <img
                  src={getFileTypeIcon(file.type)}
                  alt={'type'}
                  className={styles.typeIcon}
                />
                <EditableFileName file={file} index={index}/>
                <img
                  src={remove}
                  alt={'remove'}
                  className={styles.removeIcon}
                  onClick={() => {
                    onRemoveFile(file.name)
                  }}
                />
              </div>
            ))}
          </>
        ) : <EmptyView customContainerClassName={styles.empty}/>}
      </div>
      <div className={styles.buttonsContainer}>
        <ButtonCommon
          label={'המשך'}
          onClick={() => {
            handleChange(fieldId);
            handleCloseModal();
          }}
          disabled={files.length === 0}
          customContainerStyles={styles.buttonSubmitContainer}
        />
        <ButtonFileUpload
          fieldId={fieldId}
          onFileSelected={onFileSelected}
          multiple={multiple}
          setFiles={setFiles}
          {...rest}
        />
        <ButtonCommon
          label={'סגירה'}
          onClick={handleCloseModal}
          customContainerStyles={styles.buttonCancelContainer}
          customLabelStyles={styles.buttonCancelLabel}
        />
      </div>
    </div>
  );
};

const ButtonFileUpload = ({
                            fieldId,
                            setFiles,
                            multiple = false,
                            ...rest
                          }) => {
  const selectedFile = useRef(null);
  const handleChange = () => {
    const selected = multiple
      ? Object.values(selectedFile.current.files)
      : [selectedFile.current.files[0]];

    setFiles(selected);
  };

  return (
    <label className={styles.buttonUploadContainer}>
      {'בחירת קובץ'}
      <img src={fileIcon} alt="file" className={styles.icon}/>
      <input
        id={fieldId}
        type="file"
        ref={selectedFile}
        onChange={handleChange}
        multiple={multiple}
        hidden
        {...rest}
      />
    </label>
  );
};

FieldPasteFile.propTypes = {
  fieldId: PropTypes.string.isRequired,
  triggerType: PropTypes.string,
  fileUploaded: PropTypes.bool,
  onFileSelected: PropTypes.func,
  multiple: PropTypes.bool,
};

export default FieldPasteFile;
